/**
 * IndexedDB Schema for Booth App
 * 
 * Stores local data for offline operation:
 * - Photos: Captured photo blobs
 * - Frames: Cached frame images
 * - SyncQueue: Pending operations to sync
 */

export const DB_NAME = 'PhotoBoothDB';
export const DB_VERSION = 3;

export interface LocalProduct {
  productId: string;        // Primary key
  categoryId: string;
  name: string;
  description: string;
  shotCount: number;
  printCount: number;
  paperType: 'collage_4x6' | 'strip';
  basePrice: number;        // Base product price (always available)
  price: number;            // Effective price (outlet price if available, else base price)
  originalPrice: number;    // Original price for discount display
  hasOutletPricing: boolean; // Whether outlet-specific pricing exists
  isActive: boolean;
  syncedAt: number;
  /** Type of product. Defaults to 'session' for existing data. */
  productType?: 'session' | 'addon';
  /** Override global capture config when > 0 */
  countdownSeconds?: number;
  /** Override global capture config when > 0 */
  reviewPhotoSeconds?: number;
  /** Override global capture config when > 0 */
  editCountdownSeconds?: number;
}

export interface LocalTransaction {
  transactionId: string;    // Primary key
  outletId: string;         // Outlet where transaction was created
  productId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerInstagram: string;
  paymentMethod?: 'qris' | 'cash';
  paymentStatus: 'pending' | 'completed' | 'failed' | 'expired';
  paymentAmount: number;
  qrisReference?: string;
  paidAt?: number;
  captureStatus: 'pending' | 'in_progress' | 'completed';
  editStatus: 'pending' | 'in_progress' | 'completed';
  printStatus: 'pending' | 'queued' | 'completed' | 'failed';
  createdAt: number;
  synced: boolean;
}

export interface LocalPhoto {
  photoId: string;          // Primary key
  transactionId: string;    // Index
  outletId?: string;        // Outlet where photo was edited/captured
  imageBlob: Blob;          // Stored as Blob in IndexedDB
  frameId?: string;
  sequence: number;
  capturedAt: number;
  editedAt?: number;
  uploaded: boolean;
  slotIndex?: number;       // Layout slot assignment for photo arrangement
}

export interface LocalFrame {
  frameId: string;          // Primary key
  name: string;
  categoryId: string;       // Index
  imageBlob: Blob;          // Cached frame image
  thumbnailBlob?: Blob;
  paperType: 'collage_4x6' | 'strip';
  isActive: boolean;
  syncedAt: number;
}

export interface LocalOutlet {
  outletId: string;         // Primary key
  name: string;
  location: string;
  address?: string;
  status: 'active' | 'inactive';
  configuration?: Record<string, unknown>;
  syncedAt: number;
}

export interface SyncQueueItem {
  queueId?: number;         // Auto-increment primary key
  entityType: 'transaction' | 'photo' | 'product' | 'frame';
  entityId: string;
  operation: 'create' | 'update' | 'delete';
  payload: string;          // JSON stringified
  createdAt: number;
  retryCount: number;
  lastError?: string;
}

export const STORE_NAMES = {
  PHOTOS: 'photos',
  FRAMES: 'frames',
  SYNC_QUEUE: 'syncQueue',
  OUTLETS: 'outlets',
} as const;

export type StoreName = typeof STORE_NAMES[keyof typeof STORE_NAMES];
